import { useState, useEffect, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogClose } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Progress } from "@/components/ui/progress";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import AddToEHR from "@/components/AddToEHR";
import GoalsOfCareChatbot from "@/components/GoalsOfCareChatbot";
import {
  UserPlus, Users, Clock, CheckCircle2, XCircle, AlertTriangle, Search, Filter, Plus, Eye, Edit, Trash2,
  Building2, Stethoscope, Home, Phone, Mail, Calendar, FileText, TrendingUp, RefreshCw, ChevronRight,
  User, Activity, ClipboardList, FileUp, Upload, Download, Brain, Heart, Shield, Pen, Send, Link2,
  Loader2, ExternalLink, FileSignature, MessageSquare, BarChart3, AlertCircle, Info, CheckCircle,
  Target, Zap, Package, Truck, ArrowRight, ArrowUpRight, Clock3, Timer, PieChart, LineChart
} from "lucide-react";

// Status configurations
const STATUS_CONFIG = {
  pending: { label: "Pending", color: "bg-yellow-100 text-yellow-800", icon: Clock },
  under_review: { label: "Under Review", color: "bg-blue-100 text-blue-800", icon: Eye },
  eligibility_check: { label: "Eligibility Check", color: "bg-purple-100 text-purple-800", icon: Brain },
  awaiting_signature: { label: "Awaiting Signature", color: "bg-indigo-100 text-indigo-800", icon: FileSignature },
  accepted: { label: "Accepted", color: "bg-green-100 text-green-800", icon: CheckCircle2 },
  scheduling: { label: "Scheduling", color: "bg-cyan-100 text-cyan-800", icon: Calendar },
  admitted: { label: "Admitted", color: "bg-teal-100 text-teal-800", icon: Home },
  rejected: { label: "Rejected", color: "bg-red-100 text-red-800", icon: XCircle },
  waitlisted: { label: "Waitlisted", color: "bg-orange-100 text-orange-800", icon: Clock3 },
  cancelled: { label: "Cancelled", color: "bg-gray-100 text-gray-800", icon: XCircle }
};

const PRIORITY_CONFIG = {
  urgent: { label: "Urgent", color: "bg-red-500 text-white" },
  high: { label: "High", color: "bg-orange-500 text-white" },
  normal: { label: "Normal", color: "bg-blue-500 text-white" },
  low: { label: "Low", color: "bg-gray-500 text-white" }
};

const ELIGIBILITY_STATUS = {
  eligible: { label: "Eligible", color: "bg-green-100 text-green-800", icon: CheckCircle2 },
  likely_eligible: { label: "Likely Eligible", color: "bg-blue-100 text-blue-800", icon: Info },
  possible: { label: "Possible", color: "bg-yellow-100 text-yellow-800", icon: AlertCircle },
  not_eligible: { label: "Not Eligible", color: "bg-red-100 text-red-800", icon: XCircle },
  pending_review: { label: "Pending Review", color: "bg-purple-100 text-purple-800", icon: Clock }
};

const SIGNATURE_STATUS = {
  pending: { label: "Pending", color: "bg-yellow-100 text-yellow-800" },
  partially_signed: { label: "Partially Signed", color: "bg-blue-100 text-blue-800" },
  completed: { label: "Completed", color: "bg-green-100 text-green-800" },
  expired: { label: "Expired", color: "bg-red-100 text-red-800" },
  voided: { label: "Voided", color: "bg-gray-100 text-gray-800" }
};

export default function IntegratedReferralPortal() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("dashboard");
  
  // Dashboard & Analytics State
  const [dashboardStats, setDashboardStats] = useState(null);
  const [analyticsData, setAnalyticsData] = useState(null);
  const [timeToAdmissionData, setTimeToAdmissionData] = useState([]);
  const [nonAdmissionReasons, setNonAdmissionReasons] = useState([]);
  
  // Referrals State
  const [referrals, setReferrals] = useState([]);
  const [totalReferrals, setTotalReferrals] = useState(0);
  const [selectedReferral, setSelectedReferral] = useState(null);
  const [showReferralDetail, setShowReferralDetail] = useState(false);
  const [assignableStaff, setAssignableStaff] = useState([]);
  
  // Filters
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [priorityFilter, setPriorityFilter] = useState("all");
  
  // Eligibility Assessment State
  const [pendingAssessments, setPendingAssessments] = useState([]);
  const [selectedAssessment, setSelectedAssessment] = useState(null);
  const [showAssessmentDetail, setShowAssessmentDetail] = useState(false);
  const [lcdCriteria, setLcdCriteria] = useState(null);
  
  // Document Upload State
  const [referralDocuments, setReferralDocuments] = useState([]);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  
  // Electronic Signature State
  const [pendingSignatures, setPendingSignatures] = useState([]);
  const [showSignDialog, setShowSignDialog] = useState(false);
  const [signingPackage, setSigningPackage] = useState(null);
  const [signatureInput, setSignatureInput] = useState("");
  
  // Portal Integrations State
  const [portalIntegrations, setPortalIntegrations] = useState([]);
  const [inboundReferrals, setInboundReferrals] = useState([]);
  
  // Scheduling State
  const [schedulingQueue, setSchedulingQueue] = useState([]);
  const [showScheduleDialog, setShowScheduleDialog] = useState(false);
  const [schedulingReferral, setSchedulingReferral] = useState(null);
  
  // Messaging State
  const [messages, setMessages] = useState([]);
  const [showMessageDialog, setShowMessageDialog] = useState(false);
  const [newMessage, setNewMessage] = useState({ recipient: "", subject: "", body: "" });
  
  // New Referral Dialog
  const [showNewReferral, setShowNewReferral] = useState(false);
  const [newReferral, setNewReferral] = useState({
    patient: { name: "", date_of_birth: "", phone: "", primary_diagnosis: "", prognosis: "", physician_name: "", physician_npi: "" },
    source: "hospital",
    source_organization: "",
    source_contact_name: "",
    source_contact_phone: "",
    priority: "normal",
    reason_for_referral: "",
    icd10_codes: ""
  });
  
  // New Assessment Dialog
  const [showNewAssessment, setShowNewAssessment] = useState(false);
  const [newAssessmentData, setNewAssessmentData] = useState({
    referral_id: "",
    patient_name: "",
    primary_diagnosis: "",
    icd10_codes: "",
    karnofsky_score: "",
    weight_loss_percent: "",
    albumin: "",
    adl_bathing: false, adl_dressing: false, adl_feeding: false,
    adl_transfers: false, adl_continence: false, adl_ambulation: false,
    disease_progression: false,
    recent_hospitalizations: ""
  });

  const [submitting, setSubmitting] = useState(false);

  const allowedRoles = ["admin", "administrator", "staff", "nurse", "social_worker", "physician", "facility", "hospital"];

  useEffect(() => {
    if (authLoading) return;
    if (!user || !allowedRoles.includes(user?.role?.toLowerCase())) {
      navigate("/", { replace: true });
      return;
    }
    loadAllData();
  }, [user, navigate, authLoading]);

  const loadAllData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        loadDashboard(),
        loadReferrals(),
        loadPendingAssessments(),
        loadPendingSignatures(),
        loadPortalStatus(),
        loadLCDCriteria(),
        loadInboundReferrals(),
        loadAnalytics(),
        loadSchedulingQueue()
      ]);
    } catch (error) {
      console.error("Failed to load data:", error);
    } finally {
      setLoading(false);
    }
  };

  const loadDashboard = async () => {
    try {
      const [statsRes, staffRes] = await Promise.all([
        api.get("/referrals/dashboard"),
        api.get("/referrals/staff/assignable")
      ]);
      setDashboardStats(statsRes.data);
      setAssignableStaff(staffRes.data.staff || []);
    } catch (error) {
      console.error("Dashboard load error:", error);
    }
  };

  const loadReferrals = async () => {
    try {
      const params = new URLSearchParams();
      if (statusFilter !== "all") params.append("status", statusFilter);
      if (priorityFilter !== "all") params.append("priority", priorityFilter);
      if (searchTerm) params.append("search", searchTerm);
      
      const response = await api.get(`/referrals/list?${params.toString()}`);
      setReferrals(response.data.referrals || []);
      setTotalReferrals(response.data.total || 0);
    } catch (error) {
      console.error("Referrals load error:", error);
    }
  };

  const loadPendingAssessments = async () => {
    try {
      const response = await api.get("/eligibility/pending-review");
      setPendingAssessments(response.data.assessments || []);
    } catch (error) {
      console.error("Assessments load error:", error);
    }
  };

  const loadPendingSignatures = async () => {
    try {
      const response = await api.get("/esign/pending");
      setPendingSignatures(response.data.packages || []);
    } catch (error) {
      console.error("Signatures load error:", error);
    }
  };

  const loadPortalStatus = async () => {
    try {
      const response = await api.get("/portal-integrations/status");
      setPortalIntegrations(response.data.integrations || []);
    } catch (error) {
      console.error("Portal status load error:", error);
    }
  };

  const loadLCDCriteria = async () => {
    try {
      const response = await api.get("/eligibility/lcd-criteria");
      setLcdCriteria(response.data.criteria || null);
    } catch (error) {
      console.error("LCD criteria load error:", error);
    }
  };

  const loadInboundReferrals = async () => {
    try {
      const response = await api.get("/portal-integrations/inbound");
      setInboundReferrals(response.data.referrals || []);
    } catch (error) {
      console.error("Inbound referrals load error:", error);
    }
  };

  const loadAnalytics = async () => {
    try {
      // Load analytics data
      const response = await api.get("/referrals/analytics");
      setAnalyticsData(response.data);
      setTimeToAdmissionData(response.data.time_to_admission || []);
      setNonAdmissionReasons(response.data.non_admission_reasons || []);
    } catch (error) {
      // Mock analytics if endpoint doesn't exist
      setAnalyticsData({
        avg_time_to_admission_days: 3.2,
        conversion_rate: 78,
        pending_count: dashboardStats?.stats?.pending || 0,
        this_month_referrals: 45,
        this_month_admissions: 35
      });
    }
  };

  const loadSchedulingQueue = async () => {
    try {
      const response = await api.get("/referrals/scheduling-queue");
      setSchedulingQueue(response.data.queue || []);
    } catch (error) {
      // Mock scheduling queue from accepted referrals
      const acceptedRefs = referrals.filter(r => r.status === "accepted" || r.status === "scheduling");
      setSchedulingQueue(acceptedRefs);
    }
  };

  useEffect(() => {
    if (!loading) {
      loadReferrals();
    }
  }, [statusFilter, priorityFilter, searchTerm]);

  // Referral Actions
  const handleCreateReferral = async () => {
    if (!newReferral.patient.name) {
      toast.error("Patient name is required");
      return;
    }
    
    setSubmitting(true);
    try {
      const response = await api.post("/referrals/create", newReferral);
      toast.success("Referral created successfully");
      setShowNewReferral(false);
      setNewReferral({
        patient: { name: "", date_of_birth: "", phone: "", primary_diagnosis: "", prognosis: "", physician_name: "", physician_npi: "" },
        source: "hospital", source_organization: "", source_contact_name: "", source_contact_phone: "",
        priority: "normal", reason_for_referral: "", icd10_codes: ""
      });
      loadAllData();
    } catch (error) {
      toast.error("Failed to create referral");
    } finally {
      setSubmitting(false);
    }
  };

  const handleStatusUpdate = async (referralId, newStatus, notes = "") => {
    try {
      await api.put(`/referrals/${referralId}`, { status: newStatus, notes });
      toast.success(`Referral ${newStatus.replace('_', ' ')}`);
      loadAllData();
      setShowReferralDetail(false);
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  const handleAssign = async (referralId, staffId) => {
    try {
      await api.put(`/referrals/${referralId}`, { assigned_to: staffId });
      toast.success("Referral assigned");
      loadReferrals();
    } catch (error) {
      toast.error("Failed to assign referral");
    }
  };

  const viewReferralDetail = async (referralId) => {
    try {
      const response = await api.get(`/referrals/${referralId}`);
      setSelectedReferral(response.data);
      setShowReferralDetail(true);
      
      // Load documents for this referral
      try {
        const docsResponse = await api.get(`/documents/referral/${referralId}`);
        setReferralDocuments(docsResponse.data.documents || []);
      } catch {}
    } catch (error) {
      toast.error("Failed to load referral details");
    }
  };

  // Eligibility Assessment Actions
  const handleRunAssessment = async () => {
    if (!newAssessmentData.referral_id || !newAssessmentData.patient_name || !newAssessmentData.primary_diagnosis) {
      toast.error("Please fill in required fields");
      return;
    }
    
    const hospitalRecord = {
      patient_name: newAssessmentData.patient_name,
      primary_diagnosis: newAssessmentData.primary_diagnosis,
      icd10_codes: newAssessmentData.icd10_codes.split(",").map(c => c.trim()).filter(c => c),
      karnofsky_score: newAssessmentData.karnofsky_score ? parseInt(newAssessmentData.karnofsky_score) : null,
      weight_loss_percent: newAssessmentData.weight_loss_percent ? parseFloat(newAssessmentData.weight_loss_percent) : null,
      albumin: newAssessmentData.albumin ? parseFloat(newAssessmentData.albumin) : null,
      adl_bathing: newAssessmentData.adl_bathing,
      adl_dressing: newAssessmentData.adl_dressing,
      adl_feeding: newAssessmentData.adl_feeding,
      adl_transfers: newAssessmentData.adl_transfers,
      adl_continence: newAssessmentData.adl_continence,
      adl_ambulation: newAssessmentData.adl_ambulation,
      disease_progression: newAssessmentData.disease_progression,
      recent_hospitalizations: newAssessmentData.recent_hospitalizations ? parseInt(newAssessmentData.recent_hospitalizations) : 0
    };
    
    try {
      const response = await api.post("/eligibility/assess", {
        referral_id: newAssessmentData.referral_id,
        hospital_record: hospitalRecord,
        assessor_id: user.id,
        assessor_name: user.name
      });
      
      toast.success("Eligibility assessment completed!");
      setShowNewAssessment(false);
      setSelectedAssessment(response.data);
      setShowAssessmentDetail(true);
      loadPendingAssessments();
    } catch (error) {
      toast.error("Assessment failed: " + (error.response?.data?.detail || error.message));
    }
  };

  const handleApproveAssessment = async (assessmentId, approved, notes = "") => {
    try {
      await api.post("/eligibility/approve", {
        eligibility_assessment_id: assessmentId,
        approved,
        reviewer_id: user.id,
        reviewer_name: user.name,
        reviewer_notes: notes
      });
      
      toast.success(approved ? "Assessment approved!" : "Assessment rejected");
      setShowAssessmentDetail(false);
      
      // Update referral status if approved
      if (approved && selectedAssessment?.referral_id) {
        await handleStatusUpdate(selectedAssessment.referral_id, "awaiting_signature");
      }
      
      loadPendingAssessments();
    } catch (error) {
      toast.error("Action failed: " + (error.response?.data?.detail || error.message));
    }
  };

  const handleGenerateCTI = async (assessmentId) => {
    try {
      const response = await api.post("/eligibility/generate-cti", {
        eligibility_assessment_id: assessmentId,
        attending_physician: selectedAssessment?.hospital_record?.physician_name || "Attending Physician",
        attending_physician_npi: "1234567890",
        hospice_physician: "Dr. Medical Director",
        hospice_physician_npi: "0987654321"
      });
      
      toast.success("CTI document generated!");
      setSelectedAssessment({ ...selectedAssessment, cti_id: response.data.id });
      
      // Create signature package for CTI
      await createCTISignaturePackage(response.data);
    } catch (error) {
      toast.error("CTI generation failed");
    }
  };

  const createCTISignaturePackage = async (ctiDoc) => {
    try {
      await api.post("/esign/create", {
        document_type: "cti",
        referral_id: selectedAssessment?.referral_id,
        patient_name: selectedAssessment?.patient_name,
        document_title: "Certification of Terminal Illness",
        document_content: ctiDoc.certification_statement,
        required_signers: [
          { signer_id: user.id, signer_name: user.name, signer_role: "hospice_physician", signer_email: user.email }
        ],
        expires_in_hours: 72
      });
      toast.success("Signature request created");
      loadPendingSignatures();
    } catch (error) {
      console.error("Signature package creation failed:", error);
    }
  };

  // Document Upload Actions
  const handleFileUpload = async (event, referralId = null) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;
    
    const targetReferralId = referralId || selectedReferral?.id;
    const patientName = selectedReferral?.patient?.name || "Patient";
    
    if (!targetReferralId) {
      toast.error("Please select a referral first");
      return;
    }
    
    setUploadingFile(true);
    setUploadProgress(0);
    
    const formData = new FormData();
    formData.append("file", files[0]);
    formData.append("referral_id", targetReferralId);
    formData.append("patient_name", patientName);
    formData.append("document_type", "hospital_record");
    
    try {
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => Math.min(prev + 10, 90));
      }, 200);
      
      const response = await api.post("/documents/upload", formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      
      clearInterval(progressInterval);
      setUploadProgress(100);
      
      toast.success("Document uploaded! Text extraction in progress...");
      pollDocumentStatus(response.data.document_id);
    } catch (error) {
      toast.error("Upload failed: " + (error.response?.data?.detail || error.message));
    } finally {
      setUploadingFile(false);
      setUploadProgress(0);
    }
  };

  const pollDocumentStatus = async (documentId, attempts = 0) => {
    if (attempts > 20) {
      toast.info("Document processing taking longer than expected");
      return;
    }
    
    try {
      const response = await api.get(`/documents/${documentId}`);
      if (response.data.extraction_status === "complete") {
        toast.success("Document processed! Clinical data extracted.");
        if (selectedReferral?.id) {
          const docsResponse = await api.get(`/documents/referral/${selectedReferral.id}`);
          setReferralDocuments(docsResponse.data.documents || []);
        }
      } else if (response.data.extraction_status === "failed") {
        toast.error("Document extraction failed");
      } else {
        setTimeout(() => pollDocumentStatus(documentId, attempts + 1), 2000);
      }
    } catch (error) {
      console.error("Polling error:", error);
    }
  };

  // Signature Actions
  const handleSignDocument = async () => {
    if (!signatureInput.trim()) {
      toast.error("Please enter your signature");
      return;
    }
    
    try {
      await api.post("/esign/sign", {
        signature_package_id: signingPackage.id,
        signature_data: btoa(signatureInput),
        signature_type: "typed",
        consent_statement: "I agree to sign this document electronically"
      });
      
      toast.success("Document signed successfully!");
      setShowSignDialog(false);
      setSigningPackage(null);
      setSignatureInput("");
      loadPendingSignatures();
      
      // Update referral status if all signatures complete
      if (signingPackage.referral_id) {
        await handleStatusUpdate(signingPackage.referral_id, "accepted");
      }
    } catch (error) {
      toast.error("Signing failed: " + (error.response?.data?.detail || error.message));
    }
  };

  // Portal Integration Actions
  const handleImportReferral = async (inboundId) => {
    try {
      const response = await api.post(`/portal-integrations/inbound/${inboundId}/import`);
      toast.success("Referral imported successfully!");
      loadInboundReferrals();
      loadReferrals();
    } catch (error) {
      toast.error("Import failed");
    }
  };

  const handleSyncPortal = async (portalType) => {
    try {
      await api.post(`/portal-integrations/sync/${portalType}`);
      toast.success(`Sync with ${portalType} initiated`);
    } catch (error) {
      toast.error("Sync failed");
    }
  };

  // Scheduling Actions
  const handleScheduleAdmission = async (referralId, scheduleData) => {
    try {
      await api.post(`/referrals/${referralId}/schedule-admission`, scheduleData);
      toast.success("Admission scheduled!");
      await handleStatusUpdate(referralId, "scheduling");
      setShowScheduleDialog(false);
      loadSchedulingQueue();
    } catch (error) {
      toast.error("Scheduling failed");
    }
  };

  // Messaging Actions
  const handleSendMessage = async () => {
    if (!newMessage.recipient || !newMessage.body) {
      toast.error("Please fill in recipient and message");
      return;
    }
    
    try {
      await api.post("/referrals/messages/send", {
        ...newMessage,
        referral_id: selectedReferral?.id,
        sender_id: user.id,
        sender_name: user.name
      });
      toast.success("Message sent!");
      setShowMessageDialog(false);
      setNewMessage({ recipient: "", subject: "", body: "" });
    } catch (error) {
      toast.error("Failed to send message");
    }
  };

  // Add to EHR Action
  const handleAddToEHR = async (referralId) => {
    try {
      await api.post(`/referrals/${referralId}/add-to-ehr`);
      toast.success("Patient added to EHR!");
      await handleStatusUpdate(referralId, "admitted");
    } catch (error) {
      toast.error("Failed to add to EHR");
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-teal-50 via-white to-cyan-50 flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="h-8 w-8 animate-spin mx-auto text-teal-600 mb-4" />
          <p className="text-muted-foreground">Loading Referral Portal...</p>
        </div>
      </div>
    );
  }

  const stats = dashboardStats?.stats || {};

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900" data-testid="integrated-referral-portal">
      {/* Header */}
      <div className="bg-gradient-to-r from-teal-600 to-cyan-600 text-white py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-white/20 rounded-xl">
                <ClipboardList className="h-8 w-8" />
              </div>
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold">Referral-to-Admission Portal</h1>
                <p className="text-teal-100">Complete intake management with CGS LCD eligibility & e-signatures</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <AddToEHR buttonVariant="default" buttonSize="default" buttonText="Add to EHR" className="bg-white/20 hover:bg-white/30 border-white/30" />
              <Button onClick={() => setShowNewReferral(true)} className="bg-white text-teal-600 hover:bg-teal-50" data-testid="new-referral-btn">
                <Plus className="h-4 w-4 mr-2" />New Referral
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Quick Stats Row */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3 mb-6">
          <QuickStatCard icon={<ClipboardList className="h-5 w-5" />} label="Total" value={stats.total || 0} color="teal" />
          <QuickStatCard icon={<Clock className="h-5 w-5" />} label="Pending" value={stats.pending || 0} color="yellow" />
          <QuickStatCard icon={<Brain className="h-5 w-5" />} label="Eligibility" value={pendingAssessments.length} color="purple" />
          <QuickStatCard icon={<FileSignature className="h-5 w-5" />} label="Signatures" value={pendingSignatures.length} color="indigo" />
          <QuickStatCard icon={<CheckCircle2 className="h-5 w-5" />} label="Accepted" value={stats.accepted || 0} color="green" />
          <QuickStatCard icon={<Calendar className="h-5 w-5" />} label="Scheduling" value={schedulingQueue.length} color="cyan" />
          <QuickStatCard icon={<Home className="h-5 w-5" />} label="Admitted" value={stats.admitted || 0} color="teal" />
          <QuickStatCard icon={<ExternalLink className="h-5 w-5" />} label="Inbound" value={inboundReferrals.length} color="blue" />
        </div>

        {/* Urgent Alert Banner */}
        {stats.urgent_pending > 0 && (
          <Alert className="mb-6 bg-red-500/20 border-red-500/50 text-white">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              <strong>{stats.urgent_pending} urgent referral(s)</strong> require immediate attention!
            </AlertDescription>
          </Alert>
        )}

        {/* Main Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <TabsList className="bg-slate-800 border border-slate-700 p-1 flex-wrap">
            <TabsTrigger value="dashboard" className="data-[state=active]:bg-teal-600 text-white">
              <BarChart3 className="h-4 w-4 mr-2" />Dashboard
            </TabsTrigger>
            <TabsTrigger value="referrals" className="data-[state=active]:bg-teal-600 text-white">
              <ClipboardList className="h-4 w-4 mr-2" />Referrals
              {stats.pending > 0 && <Badge className="ml-2 bg-yellow-500">{stats.pending}</Badge>}
            </TabsTrigger>
            <TabsTrigger value="eligibility" className="data-[state=active]:bg-teal-600 text-white">
              <Brain className="h-4 w-4 mr-2" />Eligibility
              {pendingAssessments.length > 0 && <Badge className="ml-2 bg-purple-500">{pendingAssessments.length}</Badge>}
            </TabsTrigger>
            <TabsTrigger value="signatures" className="data-[state=active]:bg-teal-600 text-white">
              <FileSignature className="h-4 w-4 mr-2" />E-Signatures
              {pendingSignatures.length > 0 && <Badge className="ml-2 bg-indigo-500">{pendingSignatures.length}</Badge>}
            </TabsTrigger>
            <TabsTrigger value="scheduling" className="data-[state=active]:bg-teal-600 text-white">
              <Calendar className="h-4 w-4 mr-2" />Scheduling
            </TabsTrigger>
            <TabsTrigger value="portals" className="data-[state=active]:bg-teal-600 text-white">
              <Link2 className="h-4 w-4 mr-2" />Integrations
            </TabsTrigger>
            <TabsTrigger value="analytics" className="data-[state=active]:bg-teal-600 text-white">
              <LineChart className="h-4 w-4 mr-2" />Analytics
            </TabsTrigger>
          </TabsList>

          {/* Dashboard Tab */}
          <TabsContent value="dashboard" className="space-y-6" data-testid="dashboard-tab">
            <div className="grid lg:grid-cols-3 gap-6">
              {/* Pipeline Overview */}
              <Card className="bg-slate-800 border-slate-700 lg:col-span-2">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <Target className="h-5 w-5 text-teal-400" />
                    Referral Pipeline
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {[
                      { stage: "Pending", count: stats.pending || 0, color: "bg-yellow-500", next: "Under Review" },
                      { stage: "Under Review", count: stats.under_review || 0, color: "bg-blue-500", next: "Eligibility" },
                      { stage: "Eligibility Check", count: pendingAssessments.length, color: "bg-purple-500", next: "Signatures" },
                      { stage: "Awaiting Signature", count: pendingSignatures.length, color: "bg-indigo-500", next: "Accepted" },
                      { stage: "Accepted", count: stats.accepted || 0, color: "bg-green-500", next: "Scheduling" },
                      { stage: "Scheduling", count: schedulingQueue.length, color: "bg-cyan-500", next: "Admitted" },
                      { stage: "Admitted", count: stats.admitted || 0, color: "bg-teal-500", next: null }
                    ].map((item, idx) => (
                      <div key={idx} className="flex items-center gap-4">
                        <div className={`w-3 h-3 rounded-full ${item.color}`} />
                        <div className="flex-1">
                          <div className="flex items-center justify-between">
                            <span className="text-slate-300">{item.stage}</span>
                            <span className="text-white font-bold">{item.count}</span>
                          </div>
                          <Progress value={item.count > 0 ? Math.min(item.count * 10, 100) : 0} className="h-2 mt-1" />
                        </div>
                        {item.next && <ArrowRight className="h-4 w-4 text-slate-500" />}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Quick Actions */}
              <Card className="bg-slate-800 border-slate-700">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <Zap className="h-5 w-5 text-yellow-400" />
                    Quick Actions
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <Button className="w-full justify-between bg-teal-600 hover:bg-teal-700" onClick={() => setShowNewReferral(true)}>
                    <span className="flex items-center"><Plus className="h-4 w-4 mr-2" />New Referral</span>
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                  <Button className="w-full justify-between bg-purple-600 hover:bg-purple-700" onClick={() => setShowNewAssessment(true)}>
                    <span className="flex items-center"><Brain className="h-4 w-4 mr-2" />Run Assessment</span>
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                  <Button className="w-full justify-between bg-slate-700 hover:bg-slate-600" onClick={() => setActiveTab("eligibility")}>
                    <span className="flex items-center"><Eye className="h-4 w-4 mr-2" />Review Pending ({pendingAssessments.length})</span>
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                  <Button className="w-full justify-between bg-slate-700 hover:bg-slate-600" onClick={() => setActiveTab("signatures")}>
                    <span className="flex items-center"><FileSignature className="h-4 w-4 mr-2" />Sign Documents ({pendingSignatures.length})</span>
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                  <Button className="w-full justify-between bg-slate-700 hover:bg-slate-600" onClick={() => navigate("/hospice-ehr/command-center")}>
                    <span className="flex items-center"><Activity className="h-4 w-4 mr-2" />EHR Command Center</span>
                    <ExternalLink className="h-4 w-4" />
                  </Button>
                </CardContent>
              </Card>
            </div>

            {/* Recent Activity & Inbound */}
            <div className="grid lg:grid-cols-2 gap-6">
              <Card className="bg-slate-800 border-slate-700">
                <CardHeader>
                  <CardTitle className="text-white">Recent Referrals</CardTitle>
                </CardHeader>
                <CardContent>
                  <ScrollArea className="h-[250px]">
                    <div className="space-y-3">
                      {referrals.slice(0, 5).map((referral) => (
                        <div key={referral.id} className="flex items-center justify-between p-3 bg-slate-700/50 rounded-lg cursor-pointer hover:bg-slate-700" onClick={() => viewReferralDetail(referral.id)}>
                          <div>
                            <p className="text-white font-medium">{referral.patient?.name}</p>
                            <p className="text-slate-400 text-sm">{referral.patient?.primary_diagnosis}</p>
                          </div>
                          <Badge className={STATUS_CONFIG[referral.status]?.color || "bg-gray-100"}>{STATUS_CONFIG[referral.status]?.label || referral.status}</Badge>
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                </CardContent>
              </Card>

              <Card className="bg-slate-800 border-slate-700">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <ExternalLink className="h-5 w-5 text-blue-400" />
                    Inbound from Portals ({inboundReferrals.length})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ScrollArea className="h-[250px]">
                    {inboundReferrals.length === 0 ? (
                      <p className="text-slate-400 text-center py-8">No inbound referrals</p>
                    ) : (
                      <div className="space-y-3">
                        {inboundReferrals.slice(0, 5).map((ref) => (
                          <div key={ref.id} className="flex items-center justify-between p-3 bg-slate-700/50 rounded-lg">
                            <div>
                              <p className="text-white font-medium">{ref.patient_name}</p>
                              <p className="text-slate-400 text-sm">{ref.referring_facility}</p>
                            </div>
                            <Button size="sm" variant="outline" className="border-slate-600" onClick={() => handleImportReferral(ref.id)}>Import</Button>
                          </div>
                        ))}
                      </div>
                    )}
                  </ScrollArea>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Referrals Tab */}
          <TabsContent value="referrals" className="space-y-4" data-testid="referrals-tab">
            {/* Filters */}
            <Card className="bg-slate-800 border-slate-700">
              <CardContent className="p-4">
                <div className="flex flex-wrap items-center gap-4">
                  <div className="flex-1 min-w-[200px]">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                      <Input placeholder="Search patient name..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10 bg-slate-700 border-slate-600 text-white" />
                    </div>
                  </div>
                  <Select value={statusFilter} onValueChange={setStatusFilter}>
                    <SelectTrigger className="w-[150px] bg-slate-700 border-slate-600 text-white"><SelectValue placeholder="Status" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Status</SelectItem>
                      {Object.entries(STATUS_CONFIG).map(([key, val]) => <SelectItem key={key} value={key}>{val.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Select value={priorityFilter} onValueChange={setPriorityFilter}>
                    <SelectTrigger className="w-[140px] bg-slate-700 border-slate-600 text-white"><SelectValue placeholder="Priority" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Priority</SelectItem>
                      {Object.entries(PRIORITY_CONFIG).map(([key, val]) => <SelectItem key={key} value={key}>{val.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Button variant="outline" className="border-slate-600 text-slate-300" onClick={loadReferrals}><RefreshCw className="h-4 w-4" /></Button>
                </div>
              </CardContent>
            </Card>

            {/* Referral List */}
            <Card className="bg-slate-800 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white">Referrals ({totalReferrals})</CardTitle>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[500px]">
                  <div className="space-y-3">
                    {referrals.map((referral) => {
                      const statusConfig = STATUS_CONFIG[referral.status] || STATUS_CONFIG.pending;
                      const priorityConfig = PRIORITY_CONFIG[referral.priority] || PRIORITY_CONFIG.normal;
                      return (
                        <div key={referral.id} className="p-4 bg-slate-700/50 rounded-lg hover:bg-slate-700 cursor-pointer transition-colors" onClick={() => viewReferralDetail(referral.id)} data-testid={`referral-${referral.id}`}>
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-4">
                              <div className="h-12 w-12 rounded-full bg-teal-500/20 flex items-center justify-center">
                                <User className="h-6 w-6 text-teal-400" />
                              </div>
                              <div>
                                <div className="flex items-center gap-2">
                                  <p className="font-semibold text-white">{referral.patient?.name}</p>
                                  <Badge className={priorityConfig.color}>{priorityConfig.label}</Badge>
                                </div>
                                <p className="text-sm text-slate-400">{referral.patient?.primary_diagnosis} • {referral.source_organization || referral.source}</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-4">
                              {referral.assigned_to_name && <span className="text-sm text-slate-400">Assigned: {referral.assigned_to_name}</span>}
                              <Badge className={statusConfig.color}>{statusConfig.label}</Badge>
                              <ChevronRight className="h-5 w-5 text-slate-400" />
                            </div>
                          </div>
                        </div>
                      );
                    })}
                    {referrals.length === 0 && (
                      <div className="text-center py-12 text-slate-400">
                        <ClipboardList className="h-12 w-12 mx-auto mb-4 opacity-50" />
                        <p>No referrals found</p>
                      </div>
                    )}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Eligibility Tab */}
          <TabsContent value="eligibility" className="space-y-4" data-testid="eligibility-tab">
            <div className="grid lg:grid-cols-3 gap-6">
              <Card className="lg:col-span-2 bg-slate-800 border-slate-700">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-white flex items-center gap-2"><Clock className="h-5 w-5 text-purple-400" />Pending Reviews</CardTitle>
                    <Button size="sm" onClick={() => setShowNewAssessment(true)} className="bg-purple-600 hover:bg-purple-700"><Plus className="h-4 w-4 mr-2" />New Assessment</Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <ScrollArea className="h-[400px]">
                    {pendingAssessments.length === 0 ? (
                      <div className="text-center py-8 text-slate-400"><CheckCircle2 className="h-12 w-12 mx-auto mb-3 opacity-50" /><p>No pending reviews</p></div>
                    ) : (
                      <div className="space-y-3">
                        {pendingAssessments.map((assessment) => (
                          <div key={assessment.id} className="p-4 bg-slate-700/50 rounded-lg hover:bg-slate-700 cursor-pointer transition-colors" onClick={() => { setSelectedAssessment(assessment); setShowAssessmentDetail(true); }} data-testid={`assessment-${assessment.id}`}>
                            <div className="flex items-center justify-between">
                              <div>
                                <p className="font-medium text-white">{assessment.patient_name}</p>
                                <p className="text-sm text-slate-400">{assessment.primary_diagnosis}</p>
                              </div>
                              <div className="flex items-center gap-2">
                                <Badge className={ELIGIBILITY_STATUS[assessment.eligibility_status]?.color || "bg-gray-100"}>{ELIGIBILITY_STATUS[assessment.eligibility_status]?.label || assessment.eligibility_status}</Badge>
                                <span className="text-sm text-slate-400">{Math.round(assessment.confidence_score * 100)}%</span>
                                <ChevronRight className="h-4 w-4 text-slate-400" />
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </ScrollArea>
                </CardContent>
              </Card>

              <Card className="bg-slate-800 border-slate-700">
                <CardHeader><CardTitle className="text-white flex items-center gap-2"><Brain className="h-5 w-5 text-teal-400" />CGS LCD Criteria</CardTitle></CardHeader>
                <CardContent>
                  <ScrollArea className="h-[400px]">
                    {lcdCriteria ? (
                      <div className="space-y-4">
                        <div>
                          <h4 className="font-medium text-sm text-white mb-2">General Decline</h4>
                          <ul className="text-xs space-y-1 text-slate-400">
                            {lcdCriteria.general_decline?.indicators?.slice(0, 5).map((ind, i) => (
                              <li key={i} className="flex items-start gap-1"><CheckCircle2 className="h-3 w-3 mt-0.5 text-teal-400 flex-shrink-0" />{ind.description}</li>
                            ))}
                          </ul>
                        </div>
                        <Separator className="bg-slate-700" />
                        <div>
                          <h4 className="font-medium text-sm text-white mb-2">Disease Categories</h4>
                          <div className="flex flex-wrap gap-1">
                            {Object.keys(lcdCriteria.disease_specific || {}).map((key) => (
                              <Badge key={key} variant="outline" className="text-xs border-slate-600 text-slate-300">{lcdCriteria.disease_specific[key].name}</Badge>
                            ))}
                          </div>
                        </div>
                      </div>
                    ) : (
                      <p className="text-center text-slate-400">Loading criteria...</p>
                    )}
                  </ScrollArea>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Signatures Tab */}
          <TabsContent value="signatures" className="space-y-4" data-testid="signatures-tab">
            <Card className="bg-slate-800 border-slate-700">
              <CardHeader><CardTitle className="text-white flex items-center gap-2"><FileSignature className="h-5 w-5 text-indigo-400" />Documents Awaiting Signature</CardTitle></CardHeader>
              <CardContent>
                {pendingSignatures.length === 0 ? (
                  <div className="text-center py-8 text-slate-400"><CheckCircle2 className="h-12 w-12 mx-auto mb-3 opacity-50" /><p>No documents awaiting signature</p></div>
                ) : (
                  <div className="space-y-3">
                    {pendingSignatures.map((pkg) => (
                      <div key={pkg.id} className="p-4 bg-slate-700/50 rounded-lg" data-testid={`signature-${pkg.id}`}>
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-medium text-white">{pkg.document_title}</p>
                            <p className="text-sm text-slate-400">Patient: {pkg.patient_name} • {pkg.document_type.replace('_', ' ')}</p>
                            <p className="text-xs text-slate-500 mt-1">Expires: {new Date(pkg.expires_at).toLocaleDateString()}</p>
                          </div>
                          <div className="flex items-center gap-2">
                            <Badge className={SIGNATURE_STATUS[pkg.status]?.color || "bg-gray-100"}>{SIGNATURE_STATUS[pkg.status]?.label || pkg.status}</Badge>
                            <Button size="sm" className="bg-indigo-600 hover:bg-indigo-700" onClick={() => { setSigningPackage(pkg); setShowSignDialog(true); }}><Pen className="h-4 w-4 mr-1" />Sign</Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Scheduling Tab */}
          <TabsContent value="scheduling" className="space-y-4" data-testid="scheduling-tab">
            <Card className="bg-slate-800 border-slate-700">
              <CardHeader><CardTitle className="text-white flex items-center gap-2"><Calendar className="h-5 w-5 text-cyan-400" />Admission Scheduling Queue</CardTitle></CardHeader>
              <CardContent>
                {schedulingQueue.length === 0 ? (
                  <div className="text-center py-8 text-slate-400"><Calendar className="h-12 w-12 mx-auto mb-3 opacity-50" /><p>No referrals awaiting scheduling</p></div>
                ) : (
                  <div className="space-y-3">
                    {schedulingQueue.map((ref) => (
                      <div key={ref.id} className="p-4 bg-slate-700/50 rounded-lg">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-medium text-white">{ref.patient?.name}</p>
                            <p className="text-sm text-slate-400">{ref.patient?.primary_diagnosis}</p>
                          </div>
                          <div className="flex items-center gap-2">
                            <Button size="sm" variant="outline" className="border-slate-600 text-slate-300" onClick={() => { setSchedulingReferral(ref); setShowScheduleDialog(true); }}><Calendar className="h-4 w-4 mr-1" />Schedule</Button>
                            <Button size="sm" className="bg-teal-600 hover:bg-teal-700" onClick={() => handleAddToEHR(ref.id)}><Home className="h-4 w-4 mr-1" />Add to EHR</Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Portal Integrations Tab */}
          <TabsContent value="portals" className="space-y-4" data-testid="portals-tab">
            <div className="grid lg:grid-cols-2 gap-6">
              <Card className="bg-slate-800 border-slate-700">
                <CardHeader><CardTitle className="text-white flex items-center gap-2"><Link2 className="h-5 w-5 text-teal-400" />Portal Integrations</CardTitle></CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {["careport", "aidin", "navihealth", "epic"].map((portal) => {
                      const integration = portalIntegrations.find(p => p.portal_type === portal);
                      return (
                        <div key={portal} className="flex items-center justify-between p-3 bg-slate-700/50 rounded-lg">
                          <div className="flex items-center gap-3">
                            <div className={`w-2 h-2 rounded-full ${integration?.status === "active" ? "bg-green-500" : "bg-gray-500"}`} />
                            <div>
                              <p className="font-medium text-white capitalize">{portal}</p>
                              <p className="text-xs text-slate-400">{integration ? `Last sync: ${integration.last_sync ? new Date(integration.last_sync).toLocaleString() : "Never"}` : "Not configured"}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {integration?.status === "active" && <Button variant="outline" size="sm" className="border-slate-600" onClick={() => handleSyncPortal(portal)}><RefreshCw className="h-3 w-3 mr-1" />Sync</Button>}
                            <Button variant="ghost" size="sm" className="text-slate-400">Configure</Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-slate-800 border-slate-700">
                <CardHeader><CardTitle className="text-white flex items-center gap-2"><ExternalLink className="h-5 w-5 text-blue-400" />Inbound Referrals</CardTitle></CardHeader>
                <CardContent>
                  <ScrollArea className="h-[300px]">
                    {inboundReferrals.length === 0 ? (
                      <div className="text-center py-8 text-slate-400"><Building2 className="h-12 w-12 mx-auto mb-3 opacity-50" /><p>No inbound referrals</p></div>
                    ) : (
                      <div className="space-y-3">
                        {inboundReferrals.map((ref) => (
                          <div key={ref.id} className="p-3 bg-slate-700/50 rounded-lg" data-testid={`inbound-${ref.id}`}>
                            <div className="flex items-center justify-between mb-2">
                              <div><p className="font-medium text-white">{ref.patient_name}</p><p className="text-sm text-slate-400">{ref.primary_diagnosis}</p></div>
                              <Badge variant="outline" className="capitalize border-slate-600 text-slate-300">{ref.source_portal}</Badge>
                            </div>
                            <div className="flex items-center justify-between text-xs text-slate-400">
                              <span>From: {ref.referring_facility || "Unknown"}</span>
                              <Button size="sm" variant="outline" className="border-slate-600" onClick={() => handleImportReferral(ref.id)}>Import</Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </ScrollArea>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Analytics Tab */}
          <TabsContent value="analytics" className="space-y-4" data-testid="analytics-tab">
            <div className="grid md:grid-cols-4 gap-4">
              <Card className="bg-gradient-to-br from-teal-500/20 to-teal-600/10 border-teal-500/30">
                <CardContent className="p-4 text-center">
                  <Timer className="h-8 w-8 mx-auto text-teal-400 mb-2" />
                  <p className="text-3xl font-bold text-white">{analyticsData?.avg_time_to_admission_days || 3.2}</p>
                  <p className="text-xs text-teal-300">Avg. Days to Admission</p>
                </CardContent>
              </Card>
              <Card className="bg-gradient-to-br from-green-500/20 to-green-600/10 border-green-500/30">
                <CardContent className="p-4 text-center">
                  <TrendingUp className="h-8 w-8 mx-auto text-green-400 mb-2" />
                  <p className="text-3xl font-bold text-white">{analyticsData?.conversion_rate || 78}%</p>
                  <p className="text-xs text-green-300">Conversion Rate</p>
                </CardContent>
              </Card>
              <Card className="bg-gradient-to-br from-blue-500/20 to-blue-600/10 border-blue-500/30">
                <CardContent className="p-4 text-center">
                  <ClipboardList className="h-8 w-8 mx-auto text-blue-400 mb-2" />
                  <p className="text-3xl font-bold text-white">{analyticsData?.this_month_referrals || 45}</p>
                  <p className="text-xs text-blue-300">This Month Referrals</p>
                </CardContent>
              </Card>
              <Card className="bg-gradient-to-br from-purple-500/20 to-purple-600/10 border-purple-500/30">
                <CardContent className="p-4 text-center">
                  <Home className="h-8 w-8 mx-auto text-purple-400 mb-2" />
                  <p className="text-3xl font-bold text-white">{analyticsData?.this_month_admissions || 35}</p>
                  <p className="text-xs text-purple-300">This Month Admissions</p>
                </CardContent>
              </Card>
            </div>

            <div className="grid lg:grid-cols-2 gap-6">
              <Card className="bg-slate-800 border-slate-700">
                <CardHeader><CardTitle className="text-white">Time to Admission Trend</CardTitle></CardHeader>
                <CardContent>
                  <div className="h-[250px] flex items-center justify-center text-slate-400">
                    <div className="text-center">
                      <LineChart className="h-12 w-12 mx-auto mb-3 opacity-50" />
                      <p>Time-to-admission metrics by week</p>
                      <p className="text-xs mt-2">Average: {analyticsData?.avg_time_to_admission_days || 3.2} days</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-slate-800 border-slate-700">
                <CardHeader><CardTitle className="text-white">Non-Admission Reasons</CardTitle></CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {[
                      { reason: "Does not meet LCD criteria", count: 8, percent: 40 },
                      { reason: "Family declined", count: 5, percent: 25 },
                      { reason: "Patient stabilized", count: 4, percent: 20 },
                      { reason: "Insurance not approved", count: 3, percent: 15 }
                    ].map((item, idx) => (
                      <div key={idx}>
                        <div className="flex items-center justify-between text-sm mb-1">
                          <span className="text-slate-300">{item.reason}</span>
                          <span className="text-white font-medium">{item.count}</span>
                        </div>
                        <Progress value={item.percent} className="h-2" />
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Dialogs */}
      {/* New Referral Dialog */}
      <Dialog open={showNewReferral} onOpenChange={setShowNewReferral}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-slate-800 border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle>New Patient Referral</DialogTitle>
            <DialogDescription className="text-slate-400">Enter patient and referral information</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2"><Label>Patient Name *</Label><Input value={newReferral.patient.name} onChange={(e) => setNewReferral({...newReferral, patient: {...newReferral.patient, name: e.target.value}})} className="bg-slate-700 border-slate-600" data-testid="new-patient-name" /></div>
              <div><Label>Date of Birth</Label><Input type="date" value={newReferral.patient.date_of_birth} onChange={(e) => setNewReferral({...newReferral, patient: {...newReferral.patient, date_of_birth: e.target.value}})} className="bg-slate-700 border-slate-600" /></div>
              <div><Label>Phone</Label><Input value={newReferral.patient.phone} onChange={(e) => setNewReferral({...newReferral, patient: {...newReferral.patient, phone: e.target.value}})} className="bg-slate-700 border-slate-600" /></div>
              <div className="col-span-2"><Label>Primary Diagnosis</Label><Input value={newReferral.patient.primary_diagnosis} onChange={(e) => setNewReferral({...newReferral, patient: {...newReferral.patient, primary_diagnosis: e.target.value}})} className="bg-slate-700 border-slate-600" /></div>
              <div><Label>Prognosis</Label><Input value={newReferral.patient.prognosis} onChange={(e) => setNewReferral({...newReferral, patient: {...newReferral.patient, prognosis: e.target.value}})} className="bg-slate-700 border-slate-600" /></div>
              <div><Label>Referring Physician</Label><Input value={newReferral.patient.physician_name} onChange={(e) => setNewReferral({...newReferral, patient: {...newReferral.patient, physician_name: e.target.value}})} className="bg-slate-700 border-slate-600" /></div>
            </div>
            <Separator className="bg-slate-700" />
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Source Type</Label><Select value={newReferral.source} onValueChange={(v) => setNewReferral({...newReferral, source: v})}><SelectTrigger className="bg-slate-700 border-slate-600"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="hospital">Hospital</SelectItem><SelectItem value="physician">Physician</SelectItem><SelectItem value="facility">Facility</SelectItem><SelectItem value="family">Family</SelectItem></SelectContent></Select></div>
              <div><Label>Priority</Label><Select value={newReferral.priority} onValueChange={(v) => setNewReferral({...newReferral, priority: v})}><SelectTrigger className="bg-slate-700 border-slate-600"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="urgent">Urgent</SelectItem><SelectItem value="high">High</SelectItem><SelectItem value="normal">Normal</SelectItem><SelectItem value="low">Low</SelectItem></SelectContent></Select></div>
              <div className="col-span-2"><Label>Source Organization</Label><Input value={newReferral.source_organization} onChange={(e) => setNewReferral({...newReferral, source_organization: e.target.value})} className="bg-slate-700 border-slate-600" /></div>
            </div>
            <div><Label>Reason for Referral</Label><Textarea value={newReferral.reason_for_referral} onChange={(e) => setNewReferral({...newReferral, reason_for_referral: e.target.value})} className="bg-slate-700 border-slate-600" rows={3} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="border-slate-600" onClick={() => setShowNewReferral(false)}>Cancel</Button>
            <Button onClick={handleCreateReferral} disabled={submitting} className="bg-teal-600 hover:bg-teal-700">{submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Plus className="h-4 w-4 mr-2" />}Create Referral</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assessment Dialog */}
      <Dialog open={showAssessmentDetail} onOpenChange={setShowAssessmentDetail}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto bg-slate-800 border-slate-700 text-white">
          {selectedAssessment && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2"><Brain className="h-5 w-5" />Eligibility Assessment Review</DialogTitle>
                <DialogDescription className="text-slate-400">{selectedAssessment.patient_name} - {selectedAssessment.primary_diagnosis}</DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <Alert className={ELIGIBILITY_STATUS[selectedAssessment.eligibility_status]?.color || "bg-slate-700"}>
                  <AlertDescription className="flex items-center justify-between">
                    <span className="font-medium">Status: {ELIGIBILITY_STATUS[selectedAssessment.eligibility_status]?.label}</span>
                    <span>Confidence: {Math.round(selectedAssessment.confidence_score * 100)}%</span>
                  </AlertDescription>
                </Alert>
                <Card className="bg-slate-700/50 border-slate-600">
                  <CardHeader className="py-3"><CardTitle className="text-sm text-white">Eligibility Narrative</CardTitle></CardHeader>
                  <CardContent><ScrollArea className="h-[200px]"><pre className="text-xs whitespace-pre-wrap font-sans text-slate-300">{selectedAssessment.writeup}</pre></ScrollArea></CardContent>
                </Card>
              </div>
              <DialogFooter className="flex-col sm:flex-row gap-2">
                {selectedAssessment.status === "pending_review" && (
                  <>
                    <Button variant="outline" onClick={() => handleApproveAssessment(selectedAssessment.id, false, "Does not meet LCD criteria")} className="text-red-400 border-red-500/50"><XCircle className="h-4 w-4 mr-2" />Reject</Button>
                    <Button onClick={() => handleApproveAssessment(selectedAssessment.id, true)} className="bg-green-600 hover:bg-green-700"><CheckCircle2 className="h-4 w-4 mr-2" />Approve</Button>
                  </>
                )}
                {selectedAssessment.approval_status === "approved" && !selectedAssessment.cti_id && (
                  <Button onClick={() => handleGenerateCTI(selectedAssessment.id)} className="bg-indigo-600 hover:bg-indigo-700"><FileText className="h-4 w-4 mr-2" />Generate CTI</Button>
                )}
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* New Assessment Dialog */}
      <Dialog open={showNewAssessment} onOpenChange={setShowNewAssessment}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-slate-800 border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle>New Eligibility Assessment</DialogTitle>
            <DialogDescription className="text-slate-400">Enter clinical data to assess hospice eligibility</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Referral ID *</Label><Input value={newAssessmentData.referral_id} onChange={(e) => setNewAssessmentData({...newAssessmentData, referral_id: e.target.value})} className="bg-slate-700 border-slate-600" data-testid="assessment-referral-id" /></div>
              <div><Label>Patient Name *</Label><Input value={newAssessmentData.patient_name} onChange={(e) => setNewAssessmentData({...newAssessmentData, patient_name: e.target.value})} className="bg-slate-700 border-slate-600" /></div>
            </div>
            <div><Label>Primary Diagnosis *</Label><Input value={newAssessmentData.primary_diagnosis} onChange={(e) => setNewAssessmentData({...newAssessmentData, primary_diagnosis: e.target.value})} className="bg-slate-700 border-slate-600" /></div>
            <div><Label>ICD-10 Codes (comma-separated)</Label><Input value={newAssessmentData.icd10_codes} onChange={(e) => setNewAssessmentData({...newAssessmentData, icd10_codes: e.target.value})} className="bg-slate-700 border-slate-600" /></div>
            <div className="grid grid-cols-3 gap-4">
              <div><Label>Karnofsky Score</Label><Input type="number" min="0" max="100" value={newAssessmentData.karnofsky_score} onChange={(e) => setNewAssessmentData({...newAssessmentData, karnofsky_score: e.target.value})} className="bg-slate-700 border-slate-600" /></div>
              <div><Label>Weight Loss %</Label><Input type="number" value={newAssessmentData.weight_loss_percent} onChange={(e) => setNewAssessmentData({...newAssessmentData, weight_loss_percent: e.target.value})} className="bg-slate-700 border-slate-600" /></div>
              <div><Label>Albumin (g/dL)</Label><Input type="number" step="0.1" value={newAssessmentData.albumin} onChange={(e) => setNewAssessmentData({...newAssessmentData, albumin: e.target.value})} className="bg-slate-700 border-slate-600" /></div>
            </div>
            <div>
              <Label className="mb-2 block">ADL Dependencies</Label>
              <div className="grid grid-cols-3 gap-2">
                {[{key:"adl_bathing",label:"Bathing"},{key:"adl_dressing",label:"Dressing"},{key:"adl_feeding",label:"Feeding"},{key:"adl_transfers",label:"Transfers"},{key:"adl_continence",label:"Continence"},{key:"adl_ambulation",label:"Ambulation"}].map((adl) => (
                  <div key={adl.key} className="flex items-center space-x-2">
                    <Checkbox id={adl.key} checked={newAssessmentData[adl.key]} onCheckedChange={(checked) => setNewAssessmentData({...newAssessmentData, [adl.key]: checked})} />
                    <Label htmlFor={adl.key} className="text-sm text-slate-300">{adl.label}</Label>
                  </div>
                ))}
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox id="disease_progression" checked={newAssessmentData.disease_progression} onCheckedChange={(checked) => setNewAssessmentData({...newAssessmentData, disease_progression: checked})} />
              <Label htmlFor="disease_progression" className="text-slate-300">Disease progression despite treatment</Label>
            </div>
          </div>
          <DialogFooter>
            <DialogClose asChild><Button variant="outline" className="border-slate-600">Cancel</Button></DialogClose>
            <Button onClick={handleRunAssessment} className="bg-purple-600 hover:bg-purple-700" data-testid="run-assessment-btn"><Brain className="h-4 w-4 mr-2" />Run Assessment</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Sign Document Dialog */}
      <Dialog open={showSignDialog} onOpenChange={setShowSignDialog}>
        <DialogContent className="max-w-lg bg-slate-800 border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><FileSignature className="h-5 w-5" />Sign Document</DialogTitle>
            <DialogDescription className="text-slate-400">{signingPackage?.document_title}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <Alert className="bg-indigo-500/20 border-indigo-500/50"><Shield className="h-4 w-4" /><AlertDescription>Your signature will be encrypted and stored with a cryptographic hash.</AlertDescription></Alert>
            <div><Label>Type your full legal name to sign</Label><Input value={signatureInput} onChange={(e) => setSignatureInput(e.target.value)} placeholder="John Doe, MD" className="mt-1 bg-slate-700 border-slate-600" data-testid="signature-input" /></div>
            <div className="p-4 border border-slate-600 rounded-lg text-center"><p className="font-script text-2xl italic text-white">{signatureInput || "Your signature will appear here"}</p></div>
            <p className="text-xs text-slate-400">By signing, you agree this signature has the same legal effect as a handwritten signature.</p>
          </div>
          <DialogFooter>
            <DialogClose asChild><Button variant="outline" className="border-slate-600">Cancel</Button></DialogClose>
            <Button onClick={handleSignDocument} disabled={!signatureInput.trim()} className="bg-indigo-600 hover:bg-indigo-700" data-testid="submit-signature-btn"><Pen className="h-4 w-4 mr-2" />Sign Document</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Referral Detail Dialog */}
      <Dialog open={showReferralDetail} onOpenChange={setShowReferralDetail}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto bg-slate-800 border-slate-700 text-white">
          {selectedReferral && (
            <>
              <DialogHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <DialogTitle className="text-xl">{selectedReferral.patient?.name}</DialogTitle>
                    <DialogDescription className="text-slate-400">Referral ID: {selectedReferral.id?.slice(0, 8)}... • Created {new Date(selectedReferral.created_at).toLocaleDateString()}</DialogDescription>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge className={PRIORITY_CONFIG[selectedReferral.priority]?.color}>{PRIORITY_CONFIG[selectedReferral.priority]?.label}</Badge>
                    <Badge className={STATUS_CONFIG[selectedReferral.status]?.color}>{STATUS_CONFIG[selectedReferral.status]?.label}</Badge>
                  </div>
                </div>
              </DialogHeader>
              <div className="grid md:grid-cols-2 gap-4 py-4">
                <Card className="bg-slate-700/50 border-slate-600">
                  <CardHeader className="pb-2"><CardTitle className="text-sm text-white flex items-center gap-2"><User className="h-4 w-4" />Patient Details</CardTitle></CardHeader>
                  <CardContent className="space-y-2 text-sm text-slate-300">
                    <div><span className="text-slate-400">DOB:</span> {selectedReferral.patient?.date_of_birth || "N/A"}</div>
                    <div><span className="text-slate-400">Phone:</span> {selectedReferral.patient?.phone || "N/A"}</div>
                    <div><span className="text-slate-400">Diagnosis:</span> {selectedReferral.patient?.primary_diagnosis || "N/A"}</div>
                    <div><span className="text-slate-400">Prognosis:</span> {selectedReferral.patient?.prognosis || "N/A"}</div>
                    <div><span className="text-slate-400">Physician:</span> {selectedReferral.patient?.physician_name || "N/A"}</div>
                  </CardContent>
                </Card>
                <Card className="bg-slate-700/50 border-slate-600">
                  <CardHeader className="pb-2"><CardTitle className="text-sm text-white flex items-center gap-2"><Building2 className="h-4 w-4" />Referral Source</CardTitle></CardHeader>
                  <CardContent className="space-y-2 text-sm text-slate-300">
                    <div><span className="text-slate-400">Source:</span> {selectedReferral.source}</div>
                    <div><span className="text-slate-400">Organization:</span> {selectedReferral.source_organization || "N/A"}</div>
                    <div><span className="text-slate-400">Contact:</span> {selectedReferral.source_contact_name || "N/A"}</div>
                    <div><span className="text-slate-400">Phone:</span> {selectedReferral.source_contact_phone || "N/A"}</div>
                  </CardContent>
                </Card>
                <Card className="md:col-span-2 bg-slate-700/50 border-slate-600">
                  <CardHeader className="pb-2"><CardTitle className="text-sm text-white">Reason for Referral</CardTitle></CardHeader>
                  <CardContent><p className="text-sm text-slate-300">{selectedReferral.reason_for_referral || "No reason provided"}</p></CardContent>
                </Card>
                {/* Document Upload Section */}
                <Card className="md:col-span-2 bg-slate-700/50 border-slate-600">
                  <CardHeader className="pb-2"><CardTitle className="text-sm text-white flex items-center gap-2"><FileUp className="h-4 w-4" />Documents ({referralDocuments.length})</CardTitle></CardHeader>
                  <CardContent>
                    <div className="border-2 border-dashed border-slate-600 rounded-lg p-4 text-center mb-3">
                      <input type="file" id="referral-doc-upload" className="hidden" accept=".pdf,.png,.jpg,.jpeg,.tiff,.docx,.txt" onChange={(e) => handleFileUpload(e, selectedReferral.id)} disabled={uploadingFile} />
                      <label htmlFor="referral-doc-upload" className="cursor-pointer flex flex-col items-center">
                        {uploadingFile ? (<><Loader2 className="h-8 w-8 text-teal-400 animate-spin mb-2" /><p className="text-sm text-slate-400">Uploading...</p><Progress value={uploadProgress} className="w-32 mt-2" /></>) : (<><Upload className="h-8 w-8 text-teal-400 mb-2" /><p className="text-sm text-slate-400">Click to upload (PDF, images, DOCX)</p></>)}
                      </label>
                    </div>
                    {referralDocuments.length > 0 && (
                      <div className="space-y-2">
                        {referralDocuments.map((doc) => (
                          <div key={doc.id} className="flex items-center justify-between p-2 bg-slate-600/50 rounded">
                            <div className="flex items-center gap-2"><FileText className="h-4 w-4 text-blue-400" /><span className="text-sm text-slate-300">{doc.original_filename}</span></div>
                            <Badge className={doc.extraction_status === "complete" ? "bg-green-500/20 text-green-300" : "bg-yellow-500/20 text-yellow-300"}>{doc.extraction_status}</Badge>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
              <DialogFooter className="flex-wrap gap-2">
                <div className="flex items-center gap-2 mr-auto">
                  <Label className="text-sm text-slate-400">Assign:</Label>
                  <Select value={selectedReferral.assigned_to || ""} onValueChange={(v) => handleAssign(selectedReferral.id, v)}>
                    <SelectTrigger className="w-[180px] bg-slate-700 border-slate-600"><SelectValue placeholder="Select staff" /></SelectTrigger>
                    <SelectContent>{assignableStaff.map(staff => (<SelectItem key={staff.id} value={staff.id}>{staff.name}</SelectItem>))}</SelectContent>
                  </Select>
                </div>
                {selectedReferral.status === "pending" && (
                  <>
                    <Button variant="outline" className="border-slate-600" onClick={() => handleStatusUpdate(selectedReferral.id, "under_review")}><Eye className="h-4 w-4 mr-2" />Start Review</Button>
                    <Button className="bg-purple-600 hover:bg-purple-700" onClick={() => { setNewAssessmentData({...newAssessmentData, referral_id: selectedReferral.id, patient_name: selectedReferral.patient?.name, primary_diagnosis: selectedReferral.patient?.primary_diagnosis}); setShowReferralDetail(false); setShowNewAssessment(true); }}><Brain className="h-4 w-4 mr-2" />Run Eligibility</Button>
                  </>
                )}
                {selectedReferral.status === "under_review" && (
                  <Button className="bg-purple-600 hover:bg-purple-700" onClick={() => { setNewAssessmentData({...newAssessmentData, referral_id: selectedReferral.id, patient_name: selectedReferral.patient?.name, primary_diagnosis: selectedReferral.patient?.primary_diagnosis}); setShowReferralDetail(false); setShowNewAssessment(true); }}><Brain className="h-4 w-4 mr-2" />Run Eligibility</Button>
                )}
                {selectedReferral.status === "accepted" && (
                  <Button className="bg-teal-600 hover:bg-teal-700" onClick={() => handleAddToEHR(selectedReferral.id)}><Home className="h-4 w-4 mr-2" />Add to EHR</Button>
                )}
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Goals of Care AI Chatbot */}
      <GoalsOfCareChatbot 
        patientContext={selectedReferral ? {
          diagnosis: selectedReferral.patient?.primary_diagnosis,
          prognosis: selectedReferral.patient?.prognosis
        } : null}
        referralId={selectedReferral?.id}
      />
    </div>
  );
}

// Quick Stat Card Component
function QuickStatCard({ icon, label, value, color }) {
  const colorClasses = {
    teal: "from-teal-500/20 to-teal-600/10 border-teal-500/30 text-teal-400",
    yellow: "from-yellow-500/20 to-yellow-600/10 border-yellow-500/30 text-yellow-400",
    purple: "from-purple-500/20 to-purple-600/10 border-purple-500/30 text-purple-400",
    indigo: "from-indigo-500/20 to-indigo-600/10 border-indigo-500/30 text-indigo-400",
    green: "from-green-500/20 to-green-600/10 border-green-500/30 text-green-400",
    cyan: "from-cyan-500/20 to-cyan-600/10 border-cyan-500/30 text-cyan-400",
    blue: "from-blue-500/20 to-blue-600/10 border-blue-500/30 text-blue-400",
    red: "from-red-500/20 to-red-600/10 border-red-500/30 text-red-400"
  };
  return (
    <div className={`bg-gradient-to-br ${colorClasses[color]} border rounded-xl p-3 text-center`}>
      <div className="flex items-center justify-center mb-1">{icon}</div>
      <div className="text-xl font-bold text-white">{value}</div>
      <div className="text-xs opacity-80">{label}</div>
    </div>
  );
}
